export type FinanceFormState = {
  tax_enabled: boolean;
  tax_name: string;
  tax_id: string;
  tax_percent: number;
  tax_inclusive: boolean;
  business_details: string;
  processing_fee_bearer: 'organizer' | 'attendee';
  platform_fee_bearer: 'organizer' | 'attendee';
  payment_methods: string[];
  bank_account_name: string;
  bank_account_number: string;
  bank_account_type: string;
  bank_ifsc: string;
  bank_name: string;
  bank_address: string;
};

export type FinanceFieldErrors = {
  tax_percent?: string;
  tax_id?: string;
  payment_methods?: string;
  payment_verification?: string;
  kyc_verification?: string;
  settlement_verification?: string;
  bank_account_number?: string;
  bank_ifsc?: string;
};

export type PaymentMethodOption = {
  id: string;
  label: string;
  fee: string;
  description?: string | null;
};

export const PAYMENT_METHOD_OPTIONS: PaymentMethodOption[] = [
  { id: 'upi', label: 'UPI', fee: '0%' },
  { id: 'cards', label: 'Cards', fee: '2.0%' },
  { id: 'net_banking', label: 'Net Banking', fee: '1.5%' },
  { id: 'wallet', label: 'Wallet', fee: '1.0%' },
] ;

export function mapPaymentMethodCatalog(
  methods: Array<{ id?: string; slug?: string; label?: string; name?: string; fee?: string; processing_fee_percent?: number }> | null | undefined,
): PaymentMethodOption[] {
  if (!Array.isArray(methods) || methods.length === 0) {
    return [...PAYMENT_METHOD_OPTIONS];
  }

  return methods.map((method) => {
    const id = method.id ?? method.slug ?? '';
    const label = method.label ?? method.name ?? id;
    let fee = method.fee;
    if (!fee && typeof method.processing_fee_percent === 'number') {
      fee = Number.isInteger(method.processing_fee_percent)
        ? `${method.processing_fee_percent}%`
        : `${method.processing_fee_percent.toFixed(1)}%`;
    }

    return {
      id,
      label,
      fee: fee ?? '0%',
    };
  });
}

export function emptyFinanceForm(paymentMethodIds?: string[]): FinanceFormState {
  const defaultMethods = paymentMethodIds?.length
    ? paymentMethodIds
    : PAYMENT_METHOD_OPTIONS.map((method) => method.id);

  return {
    tax_enabled: false,
    tax_name: 'GST',
    tax_id: '',
    tax_percent: 18,
    tax_inclusive: false,
    business_details: '',
    processing_fee_bearer: 'attendee',
    platform_fee_bearer: 'attendee',
    payment_methods: defaultMethods,
    bank_account_name: '',
    bank_account_number: '',
    bank_account_type: 'savings',
    bank_ifsc: '',
    bank_name: '',
    bank_address: '',
  };
}

export function financeFormFromApi(
  data: Record<string, any> | null | undefined,
): FinanceFormState {
  const catalog = mapPaymentMethodCatalog(data?.available_payment_methods);
  const allowedIds = catalog.map((method) => method.id);
  const base = emptyFinanceForm(allowedIds);
  if (!data) return base;

  const tax = data.tax ?? {};
  const bank = data.bank ?? {};
  const selectedMethods = Array.isArray(data.payment_methods)
    ? data.payment_methods.filter((method: string) => allowedIds.includes(method))
    : [];

  return {
    tax_enabled: Boolean(tax.tax_enabled),
    tax_name: tax.tax_name ?? base.tax_name,
    tax_id: tax.tax_id ?? '',
    tax_percent: Number(tax.tax_percent ?? base.tax_percent),
    tax_inclusive: Boolean(tax.tax_inclusive),
    business_details:
      typeof tax.business_details === 'string'
        ? tax.business_details
        : tax.business_details
          ? JSON.stringify(tax.business_details)
          : '',
    processing_fee_bearer: data.processing_fee_bearer === 'organizer' ? 'organizer' : 'attendee',
    platform_fee_bearer: data.platform_fee_bearer === 'organizer' ? 'organizer' : 'attendee',
    payment_methods: selectedMethods.length ? selectedMethods : allowedIds,
    bank_account_name: bank.bank_account_name ?? '',
    bank_account_number: bank.bank_account_number ?? '',
    bank_account_type: bank.bank_account_type ?? 'savings',
    bank_ifsc: bank.bank_ifsc ?? '',
    bank_name: bank.bank_name ?? '',
    bank_address: bank.bank_address ?? '',
  };
}

export type PayoutVerificationState = {
  paymentProfileReady: boolean;
  kycApproved: boolean;
  settlementVerified: boolean;
  ready: boolean;
};

export type FinanceValidationOptions = {
  requireBankDetails?: boolean;
  payoutVerification?: PayoutVerificationState;
};

export function hasBankDetailsConfigured(form: FinanceFormState): boolean {
  return Boolean(form.bank_account_number.trim() && form.bank_ifsc.trim());
}

/** Local bank save only — never implies Razorpay Linked Account or KYC completion. */
export function localBankDetailsBadgeLabel(configured: boolean): string {
  return configured ? 'Bank details on file' : 'Add bank account';
}

const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/i;

export function normalizeBankAccountNumber(value: string): string {
  return value.replace(/\s+/g, '').replace(/\D/g, '');
}

export function normalizeBankIfsc(value: string): string {
  return value.trim().toUpperCase();
}

/** Validate bank fields only if values have been provided. */
export function validateBankDetailsFields(form: FinanceFormState): FinanceFieldErrors {
  const errors: FinanceFieldErrors = {};
  const account = normalizeBankAccountNumber(form.bank_account_number);
  const ifsc = normalizeBankIfsc(form.bank_ifsc);

  // In the wizard, bank details are optional and enforced only at payout.
  // Validate format only when values are non-empty.
  if (account && !/^\d{9,18}$/.test(account)) {
    errors.bank_account_number = 'Bank account number must be 9 to 18 digits.';
  }

  if (ifsc && !IFSC_PATTERN.test(ifsc)) {
    errors.bank_ifsc = 'IFSC must look like HDFC0001234 (11 characters).';
  }

  return errors;
}

export function validateFinanceForm(
  form: FinanceFormState,
  requirePaidSetup: boolean,
  options: FinanceValidationOptions = {},
): FinanceFieldErrors {
  const errors: FinanceFieldErrors = {};

  if (form.payment_methods.length === 0) {
    errors.payment_methods = 'Enable at least one payment method.';
  }

  // Bank details are optional in the event creation wizard and strictly enforced only when payouts/settlements are initiated
  const requireBank = Boolean(options.requireBankDetails);
  if (requireBank) {
    const account = normalizeBankAccountNumber(form.bank_account_number);
    const ifsc = normalizeBankIfsc(form.bank_ifsc);
    if (!account) {
      errors.bank_account_number = 'Bank account number is required for payout.';
    } else if (!/^\d{9,18}$/.test(account)) {
      errors.bank_account_number = 'Bank account number must be 9 to 18 digits.';
    }
    if (!ifsc) {
      errors.bank_ifsc = 'IFSC is required for payout.';
    } else if (!IFSC_PATTERN.test(ifsc)) {
      errors.bank_ifsc = 'IFSC must look like HDFC0001234 (11 characters).';
    }
  } else {
    Object.assign(errors, validateBankDetailsFields(form));
  }

  return errors;
}

export function buildEventFinancePayload(form: FinanceFormState): Record<string, unknown> {
  // Tax is configured in the admin platform panel — do not overwrite from the create-event wizard.
  return {
    processing_fee_bearer: form.processing_fee_bearer,
    platform_fee_bearer: form.platform_fee_bearer,
    payment_methods: form.payment_methods,
  };
}

export function buildOrganizerBankPayload(form: FinanceFormState): Record<string, unknown> {
  const account = normalizeBankAccountNumber(form.bank_account_number);
  const ifsc = normalizeBankIfsc(form.bank_ifsc);
  return {
    bank_account_name: form.bank_account_name.trim() || null,
    bank_account_number: account || null,
    bank_account_type: form.bank_account_type.trim() || 'savings',
    bank_ifsc: ifsc || null,
    bank_name: form.bank_name.trim() || null,
    bank_address: form.bank_address.trim() || null,
  };
}
